import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import MenuItemCard from '../components/MenuItemCard';
import { apiRequest } from '../lib/utils';

const MenuPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const selectedCategory = searchParams.get('category') || 'all';

  // Get localized category name based on current language
  const getCategoryDisplayName = (category) => {
    const lang = i18n.language;
    if (lang === 'it' && category.name_it) return category.name_it;
    if (lang === 'de' && category.name_de) return category.name_de;
    if (lang === 'fr' && category.name_fr) return category.name_fr;
    if (lang === 'ar' && category.name_ar) return category.name_ar;
    return category.name;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cats, items] = await Promise.all([
          apiRequest('/categories'),
          apiRequest('/menu')
        ]);
        setCategories(cats);
        setMenuItems(items);
      } catch (error) {
        console.error('Error fetching menu:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCategoryChange = (categoryId) => {
    if (categoryId === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams);
  };

  const filteredItems = menuItems.filter((item) => {
    const matchesCategory = selectedCategory === 'all' || item.category_id === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group items by category for display
  const groupedItems = {};
  if (selectedCategory === 'all') {
    filteredItems.forEach(item => {
      if (!groupedItems[item.category_id]) {
        groupedItems[item.category_id] = [];
      }
      groupedItems[item.category_id].push(item);
    });
  }

  return (
    <div className="min-h-screen py-8">
      <Helmet>
        <title>Menu | Tasty Bites - Halal Food Takeaway Lugano</title>
        <meta name="description" content="Browse our halal menu. Burgers, pizza, ethnic kitchen, sandwiches, desserts & more. Order online for delivery or pickup in Lugano." />
        <link rel="canonical" href="https://www.tastybiteslugano.ch/menu" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-2 font-['Barlow_Condensed']">
            {t('menu.title').split(' ')[0]} <span className="text-[#FFC107]">{t('menu.title').split(' ').slice(1).join(' ')}</span>
          </h1>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
            <Input
              type="text"
              placeholder={t('menu.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#141417] border-[#27272A] focus:border-[#FFC107] h-12"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Category Pills */}
        <ScrollArea className="w-full mb-8">
          <div className="flex gap-2 pb-4">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={`rounded-full px-6 whitespace-nowrap ${
                selectedCategory === 'all'
                  ? 'bg-[#FFC107] text-black hover:bg-[#F59E0B]'
                  : 'border-zinc-700 hover:bg-[#25252E]'
              }`}
              onClick={() => handleCategoryChange('all')}
              data-testid="category-all"
            >
              {t('menu.allCategories')}
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                className={`rounded-full px-6 whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-[#FFC107] text-black hover:bg-[#F59E0B]'
                    : 'border-zinc-700 hover:bg-[#25252E]'
                }`}
                onClick={() => handleCategoryChange(category.id)}
                data-testid={`category-btn-${category.id}`}
              >
                {getCategoryDisplayName(category)}
              </Button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Menu Items */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-[#1C1C22] rounded-xl mb-4" />
                <div className="h-4 bg-[#1C1C22] rounded w-3/4 mb-2" />
                <div className="h-4 bg-[#1C1C22] rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : selectedCategory === 'all' ? (
          // Grouped by category view — ordered by category sort order
          <div className="space-y-12">
            {categories
              .filter(cat => groupedItems[cat.id]?.length > 0)
              .map((cat) => (
              <div key={cat.id}>
                <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
                  {getCategoryDisplayName(cat)}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                  {groupedItems[cat.id].map((item) => (
                    <MenuItemCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Single category view
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {filteredItems.map((item) => (
              <MenuItemCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {/* No results */}
        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-16">
            <p className="text-zinc-400 text-lg">{t('menu.noItems')}</p>
          </div>
        )}

        {/* Image disclaimer */}
        <p className="text-zinc-500 text-xs mt-8 text-center" data-testid="menu-image-disclaimer">
          * {t('menu.imageDisclaimer') || 'Images are for illustration purposes only. Actual product may differ.'}
        </p>
      </div>
    </div>
  );
};

export default MenuPage;
